/**
 * useSyncStatus.js — React hook for monitoring offline sync state
 *
 * Provides: { isOnline, pendingCount, syncState }
 *   syncState: 'synced' | 'pending' | 'syncing' | 'offline'
 *
 * Includes a manual sync fallback for non-Chromium browsers: if the device
 * comes online and pending items exist, it asks the SW to replay after 3s
 * in case the native Background Sync event doesn't fire.
 */
import { useState, useEffect, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import db from '../utils/offlineDB';

const useSyncStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncState, setSyncState] = useState(navigator.onLine ? 'synced' : 'offline');

  // Live-query the outbox count from IndexedDB (auto-updates on changes)
  const pendingCount = useLiveQuery(
    () => db.mutationQueue.where('status').anyOf('pending', 'failed').count(),
    [],
    0 // default while loading
  );

  // ── Online / Offline listeners ──
  useEffect(() => {
    const goOnline  = () => setIsOnline(true);
    const goOffline = () => { setIsOnline(false); setSyncState('offline'); };

    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);

    return () => {
      window.removeEventListener('online',  goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // ── Derive syncState from isOnline + pendingCount ──
  useEffect(() => {
    if (!isOnline) {
      setSyncState('offline');
    } else if (pendingCount > 0) {
      setSyncState('pending');
    } else {
      setSyncState('synced');
    }
  }, [isOnline, pendingCount]);

  // ── Listen for SW SYNC_COMPLETE messages ──
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'SYNC_COMPLETE') {
        setSyncState(event.data.remaining > 0 ? 'pending' : 'synced');
      }
      if (event.data?.type === 'SYNC_STARTED') {
        setSyncState('syncing');
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', handleMessage);
  }, []);

  // ── Listen for outbox-updated custom events ──
  useEffect(() => {
    const handler = () => {
      // pendingCount is auto-updated by useLiveQuery, but we trigger
      // a syncState re-derive on the next render
    };
    window.addEventListener('outbox-updated', handler);
    return () => window.removeEventListener('outbox-updated', handler);
  }, []);

  // ── Manual sync fallback ──
  // If we come online and there are pending items, wait 3s then ask the
  // Service Worker to replay (in case the native sync event doesn't fire).
  const triggerManualSync = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      reg.active?.postMessage({ type: 'MANUAL_SYNC' });
      setSyncState('syncing');
    } catch (err) {
      console.warn('[useSyncStatus] Manual sync trigger failed:', err);
    }
  }, []);

  useEffect(() => {
    if (isOnline && pendingCount > 0 && syncState === 'pending') {
      const timer = setTimeout(() => {
        triggerManualSync();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, pendingCount, syncState, triggerManualSync]);

  return { isOnline, pendingCount, syncState };
};

export default useSyncStatus;
